import type {
  ProductCategory,
  ProductListItemData,
  ProductsStoreState,
  ProductUrlFilters
} from './types';

import {Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {useUrlFilters} from '@router';
import {useCallback, useEffect, useRef, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useAppDispatch, useAppSelector} from 'store';
import {resetCategories, resetProducts, resetSubCategories} from 'store/slices/products/slicer';
import {
  getProducts,
  getProductsCategories,
  getProductsSubCategories
} from 'store/slices/products/thunk';
import {getSelectedVendorFromStorage} from 'utils/Helper';

import ProductCategories from './components/ProductCategories/ProductCategories';
import ProductFilters from './components/ProductFilters/ProductFilters';
import ProductListItem from './components/ProductListItem/ProductListItem';
import ProductListItemSkeleton from './components/ProductListItemSkeleton/ProductListItemSkeleton';
import ProductsEmptyState from './components/ProductsEmptyState/ProductsEmptyState';
import {PRODUCT_FILTER_SCHEMA, PRODUCT_PAGE_SIZE} from './constants';

const ProductList: React.FC = () => {
  const dispatch = useAppDispatch();
  const {filters, setFilters, clearFilters} = useUrlFilters<ProductUrlFilters>({
    schema: PRODUCT_FILTER_SCHEMA
  });
  const selectedVendor = useAppSelector((state) => state.dashboard.selectedVendor);
  const vendorId = selectedVendor?.id ?? getSelectedVendorFromStorage()?.id;
  const {
    categories,
    categoriesLoading,
    subCategories,
    subCategoriesLoading,
    products,
    productsLoading
  } = useAppSelector((state) => state.products) as ProductsStoreState;
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    if (!vendorId) {
      return;
    }
    setSelectedCategoryId(null);
    setSelectedSubCategoryId(null);
    setPage(1);
    dispatch(resetCategories());
    dispatch(resetSubCategories());
    dispatch(resetProducts());
    void dispatch(getProductsCategories({vendor_id: vendorId}));
  }, [dispatch, vendorId]);

  useEffect(() => {
    if (categories.length === 0 || selectedCategoryId) {
      return;
    }
    setSelectedCategoryId(String(categories[0].id));
  }, [categories, selectedCategoryId]);

  useEffect(() => {
    if (!vendorId || !selectedCategoryId) {
      return;
    }
    setSelectedSubCategoryId(null);
    dispatch(resetSubCategories());
    void dispatch(
      getProductsSubCategories({
        vendor_id: vendorId,
        parent_id: selectedCategoryId
      })
    );
  }, [dispatch, selectedCategoryId, vendorId]);

  useEffect(() => {
    if (subCategories.length === 0 || selectedSubCategoryId) {
      return;
    }
    setSelectedSubCategoryId(String(subCategories[0].id));
  }, [selectedSubCategoryId, subCategories]);

  const fetchProducts = useCallback(
    (requestedPage: number) => {
      if (!vendorId || !selectedSubCategoryId) {
        return;
      }
      void dispatch(
        getProducts({
          vendor_id: vendorId,
          category_id: selectedSubCategoryId,
          page_size: PRODUCT_PAGE_SIZE,
          page: requestedPage,
          stock_status: filters.stock_status,
          sort: filters.sort,
          badge: filters.badge
        })
      );
    },
    [dispatch, filters.badge, filters.sort, filters.stock_status, selectedSubCategoryId, vendorId]
  );

  useEffect(() => {
    if (!selectedSubCategoryId) {
      return;
    }
    setPage(1);
    dispatch(resetProducts());
    fetchProducts(1);
  }, [dispatch, fetchProducts, selectedSubCategoryId]);

  useEffect(() => {
    if (!productsLoading) {
      loadingMoreRef.current = false;
    }
  }, [productsLoading]);

  const items = Array.isArray(products.items) ? products.items : [];
  const hasMore = items.length < (products.total_items ?? 0);

  if (!categoriesLoading && categories.length === 0) {
    return <ProductsEmptyState />;
  }

  return (
    <PageLayout
      contentId="products-list"
      header={
        <>
          <ProductFilters
            filters={filters}
            onChange={(nextFilters) => setFilters(nextFilters, {replace: true})}
            onClear={(name) => clearFilters(name, {replace: true})}
          />
          <ProductCategories
            categories={categories as ProductCategory[]}
            categoriesLoading={categoriesLoading}
            subCategories={subCategories as ProductCategory[]}
            subCategoriesLoading={subCategoriesLoading}
            selectedCategoryId={selectedCategoryId}
            selectedSubCategoryId={selectedSubCategoryId}
            onCategoryChange={setSelectedCategoryId}
            onSubCategoryChange={setSelectedSubCategoryId}
          />
        </>
      }
    >
      <InfiniteScroll
        dataLength={items.length}
        scrollableTarget="products-list"
        scrollThreshold={0.8}
        next={() => {
          if (productsLoading || loadingMoreRef.current || !hasMore) {
            return;
          }
          loadingMoreRef.current = true;
          const nextPage = page + 1;
          setPage(nextPage);
          fetchProducts(nextPage);
        }}
        hasMore={hasMore}
        loader={
          <div className="flex h-10 justify-center">
            <Loading size="medium" color="primary" />
          </div>
        }
      >
        <div className="flex flex-col">
          {productsLoading && items.length === 0
            ? Array.from({length: 5}, (_, index) => <ProductListItemSkeleton key={index} />)
            : items.map((item: ProductListItemData) => (
                <ProductListItem key={item.id} item={item} />
              ))}
        </div>
      </InfiniteScroll>
    </PageLayout>
  );
};

export default ProductList;
